import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  getDocs,
  getDoc,
  query,
  where,
  orderBy,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';
import {
  Campaign,
  CreateCampaignData,
  CampaignRecipient,
} from '../../types/campaign';

const CAMPAIGNS_COLLECTION = 'campaigns';
const CAMPAIGN_RECIPIENTS_COLLECTION = 'campaignRecipients';

function convertTimestamps(data: any): Campaign {
  return {
    ...data,
    createdAt: data.createdAt?.toDate() || new Date(),
    updatedAt: data.updatedAt?.toDate() || new Date(),
    sentAt: data.sentAt?.toDate() || undefined,
    schedule: {
      ...data.schedule,
      scheduledAt: data.schedule?.scheduledAt?.toDate() || undefined,
    },
  } as Campaign;
}

export const campaignService = {
  async create(data: CreateCampaignData, userId: string, userName: string): Promise<string> {
    const docRef = await addDoc(collection(db, CAMPAIGNS_COLLECTION), {
      ...data,
      status: data.status || 'draft',
      stats: {
        totalRecipients: 0,
        sent: 0,
        delivered: 0,
        opened: 0,
        clicked: 0,
        bounced: 0,
        failed: 0,
        unsubscribed: 0,
      },
      createdBy: userId,
      createdByName: userName,
      createdAt: serverTimestamp(),
      updatedAt: serverTimestamp(),
    });
    return docRef.id;
  },

  async update(id: string, data: Partial<Campaign>): Promise<void> {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { id: _id, createdAt: _createdAt, ...updateData } = data as any;
    await updateDoc(doc(db, CAMPAIGNS_COLLECTION, id), {
      ...updateData,
      updatedAt: serverTimestamp(),
    });
  },

  async delete(id: string): Promise<void> {
    await deleteDoc(doc(db, CAMPAIGNS_COLLECTION, id));
    
    // Delete recipients
    const recipientsQuery = query(
      collection(db, CAMPAIGN_RECIPIENTS_COLLECTION),
      where('campaignId', '==', id)
    );
    const snapshot = await getDocs(recipientsQuery);
    const deletePromises = snapshot.docs.map(d => deleteDoc(d.ref));
    await Promise.all(deletePromises);
  },

  async getAll(organizationId?: string): Promise<Campaign[]> {
    let q;
    if (organizationId) {
      q = query(
        collection(db, CAMPAIGNS_COLLECTION),
        where('organizationId', '==', organizationId),
        orderBy('createdAt', 'desc')
      );
    } else {
      q = query(
        collection(db, CAMPAIGNS_COLLECTION),
        orderBy('createdAt', 'desc')
      );
    }
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => convertTimestamps({ id: doc.id, ...doc.data() }));
  },

  async getById(id: string): Promise<Campaign | null> {
    const docSnap = await getDoc(doc(db, CAMPAIGNS_COLLECTION, id));
    if (!docSnap.exists()) return null;
    return convertTimestamps({ id: docSnap.id, ...docSnap.data() });
  },

  async updateStatus(id: string, status: Campaign['status']): Promise<void> {
    const updateData: any = { status, updatedAt: serverTimestamp() };
    if (status === 'sent') {
      updateData.sentAt = serverTimestamp();
    }
    await updateDoc(doc(db, CAMPAIGNS_COLLECTION, id), updateData);
  },

  async updateStats(id: string, stats: Campaign['stats']): Promise<void> {
    await updateDoc(doc(db, CAMPAIGNS_COLLECTION, id), { stats, updatedAt: serverTimestamp() });
  },

  async addRecipients(campaignId: string, recipients: Omit<CampaignRecipient, 'id' | 'campaignId'>[]): Promise<void> {
    const batch = recipients.map(recipient =>
      addDoc(collection(db, CAMPAIGN_RECIPIENTS_COLLECTION), {
        ...recipient,
        campaignId,
        createdAt: serverTimestamp(),
      })
    );
    await Promise.all(batch);
    
    await updateDoc(doc(db, CAMPAIGNS_COLLECTION, campaignId), {
      'stats.totalRecipients': recipients.length,
      updatedAt: serverTimestamp(),
    });
  },

  async getRecipients(campaignId: string): Promise<CampaignRecipient[]> {
    const q = query(
      collection(db, CAMPAIGN_RECIPIENTS_COLLECTION),
      where('campaignId', '==', campaignId)
    );
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      sentAt: doc.data().sentAt?.toDate(),
      deliveredAt: doc.data().deliveredAt?.toDate(),
      openedAt: doc.data().openedAt?.toDate(),
      clickedAt: doc.data().clickedAt?.toDate(),
    })) as CampaignRecipient[];
  },

  async updateRecipientStatus(
    recipientId: string,
    status: CampaignRecipient['status'],
    extra?: Partial<CampaignRecipient>
  ): Promise<void> {
    const updateData: any = { status, ...extra };
    if (status === 'sent') updateData.sentAt = serverTimestamp();
    if (status === 'delivered') updateData.deliveredAt = serverTimestamp();
    if (status === 'opened') updateData.openedAt = serverTimestamp();
    if (status === 'clicked') updateData.clickedAt = serverTimestamp();
    
    await updateDoc(doc(db, CAMPAIGN_RECIPIENTS_COLLECTION, recipientId), updateData);
  },

  async duplicate(id: string, userId: string, userName: string): Promise<string> {
    const original = await this.getById(id);
    if (!original) throw new Error('Campaign not found');
    
    const newData: CreateCampaignData = {
      name: `${original.name} (copy)`,
      type: original.type,
      audience: original.audience,
      emailContent: original.emailContent,
      smsContent: original.smsContent,
      schedule: { sendNow: false },
      organizationId: original.organizationId,
    };
    
    return this.create(newData, userId, userName);
  },
};
